import { useState, useEffect, useContext, useCallback } from "react";
import Select from "react-select";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { VerifyContext } from "app/VerifyContext";
import LeadCard from "./LeadCard";
import LeadDetailModal from "./LeadDetailModal";
import ConfirmModal from "./ConfirmModal";
import {
  listLeadTracking,
  listMyLeadTracking,
  listLeadTrackingTargets,
  listMyLeadTrackingTargets,
  updateTrackingStatus,
  updateMyTrackingStatus,
  getArchivedTrackingPreview,
  deleteArchivedTracking,
} from "../api/WatchlistApi";
import type { WatchlistLeadTrackingRecord, LeadTrackingTarget } from "../api/WatchlistApi";
import api from "api/axios";
import ViewToggle from "shared/ViewToggle";
import StatusKanbanBoard, { type KanbanColumn } from "shared/StatusKanbanBoard";

const LEADS_VIEW_KEY = "hrms_watchlist_leads_view";
const TERMINAL_STATUSES = new Set(["archived", "not_relevant"]);

interface TechManager {
  id: number;
  employee_code: string;
  name: string;
  email: string;
}

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "new", label: "New" },
  { value: "reviewing", label: "Reviewing" },
  { value: "applied", label: "Applied" },
  { value: "interviewing", label: "Interviewing" },
  { value: "offer_received", label: "Offer Received" },
  { value: "hired", label: "Hired" },
  { value: "rejected", label: "Rejected" },
  { value: "not_relevant", label: "Not Relevant" },
  { value: "archived", label: "Archived" },
];

const BOARD_COLUMNS: KanbanColumn[] = STATUS_OPTIONS
  .filter((o) => o.value !== "")
  .map((o) => ({ key: o.value, label: o.label, isTerminal: TERMINAL_STATUSES.has(o.value) }));

const STATUS_CHIP_SELECTED: Record<string, string> = {
  "":             "bg-slate-500 text-white border-slate-400",
  new:            "bg-blue-500 text-white border-blue-400",
  reviewing:      "bg-violet-500 text-white border-violet-400",
  applied:        "bg-sky-500 text-sky-950 border-sky-400",
  interviewing:   "bg-amber-500 text-amber-950 border-amber-400",
  offer_received: "bg-teal-500 text-white border-teal-400",
  hired:          "bg-emerald-500 text-emerald-950 border-emerald-400",
  rejected:       "bg-red-500 text-white border-red-400",
  not_relevant:   "bg-zinc-500 text-white border-zinc-400",
  archived:       "bg-zinc-700 text-zinc-200 border-zinc-600",
};

const SELECT_STYLES = {
  control: (base: any, state: any) => ({
    ...base,
    background: "hsl(var(--surface))",
    borderColor: state.isFocused ? "hsl(var(--primary))" : "hsl(var(--border))",
    borderRadius: "0.75rem",
    minHeight: "38px",
    boxShadow: "none",
    fontSize: "0.875rem",
    fontFamily: "Inter, sans-serif",
    color: "hsl(var(--foreground))",
    cursor: "pointer",
    "&:hover": { borderColor: "hsl(var(--primary))" },
  }),
  menu: (base: any) => ({
    ...base,
    background: "hsl(var(--surface-muted))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "0.75rem",
    overflow: "hidden",
    zIndex: 50,
  }),
  menuList: (base: any) => ({ ...base, padding: "4px" }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected ? "hsl(var(--primary))" : state.isFocused ? "hsl(var(--surface))" : "transparent",
    color: state.isSelected ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
    fontSize: "0.875rem",
    fontFamily: "Inter, sans-serif",
    borderRadius: "0.5rem",
    padding: "6px 10px",
    cursor: "pointer",
  }),
  singleValue: (base: any) => ({ ...base, color: "hsl(var(--foreground))", fontFamily: "Inter, sans-serif" }),
  input: (base: any) => ({ ...base, color: "hsl(var(--foreground))", fontFamily: "Inter, sans-serif" }),
  placeholder: (base: any) => ({ ...base, color: "hsl(var(--muted-foreground))", fontFamily: "Inter, sans-serif" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({ ...base, color: "hsl(var(--muted-foreground))", padding: "0 8px" }),
  noOptionsMessage: (base: any) => ({
    ...base,
    color: "hsl(var(--muted-foreground))",
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
  }),
};

export default function WatchlistLeadsTab() {
  const { superAdmin } = useContext(VerifyContext);
  const isAdmin = superAdmin;

  const [items, setItems] = useState<WatchlistLeadTrackingRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("new");
  const [profileFilter, setProfileFilter] = useState<number | null>(null);
  const [tmFilter, setTmFilter] = useState<number | null>(null);
  const [companyApplyOnly, setCompanyApplyOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  const [techManagers, setTechManagers] = useState<TechManager[]>([]);

  const [selectedRecord, setSelectedRecord] = useState<WatchlistLeadTrackingRecord | null>(null);
  const [archivedCount, setArchivedCount] = useState<number | null>(null);
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const [targets, setTargets] = useState<LeadTrackingTarget[]>([]);

  const [view, setView] = useState<"list" | "board">(() =>
    localStorage.getItem(LEADS_VIEW_KEY) === "list" ? "list" : "board"
  );
  const switchView = (v: "list" | "board") => {
    setView(v);
    localStorage.setItem(LEADS_VIEW_KEY, v);
  };
  // Board shows every status as its own column, each paginated independently
  // — keeps every other filter in sync with the list view.
  const fetchBoardColumn = useCallback(async (statusKey: string, page: number, pageSize: number) => {
    const params = {
      page,
      page_size: pageSize,
      status: statusKey,
      ...(profileFilter != null ? { watch_target_id: profileFilter } : {}),
      ...(isAdmin && tmFilter != null ? { employee_id: tmFilter } : {}),
      ...(isAdmin && companyApplyOnly ? { company_can_apply_only: true } : {}),
    };
    const data = isAdmin ? await listLeadTracking(params) : await listMyLeadTracking(params);
    return { items: data.items, total: data.total_count };
  }, [isAdmin, profileFilter, tmFilter, companyApplyOnly]);

  // Load tech managers for admin TM filter
  useEffect(() => {
    if (!isAdmin) return;
    api.get<TechManager[]>("/admin/linkedin-jobs/tech-managers")
      .then((res) => setTechManagers(res.data))
      .catch(() => {});
  }, [isAdmin]);

  // Full set of targets with tracking records in scope — independent of the status
  // filter/page, so a target with no leads under the current status still shows up
  // as a choice in the profile dropdown below.
  useEffect(() => {
    const fetch = isAdmin
      ? listLeadTrackingTargets({ employee_id: tmFilter ?? undefined, company_can_apply_only: companyApplyOnly || undefined })
      : listMyLeadTrackingTargets();
    fetch.then(setTargets).catch(() => setTargets([]));
  }, [isAdmin, tmFilter, companyApplyOnly]);

  // Load archived count
  useEffect(() => {
    getArchivedTrackingPreview(isAdmin)
      .then((r) => setArchivedCount(r.lead_count))
      .catch(() => {});
  }, [isAdmin]);

  // Load tracking records whenever filters/page change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = {
      page,
      page_size: 20,
      ...(profileFilter != null ? { watch_target_id: profileFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(isAdmin && tmFilter != null ? { employee_id: tmFilter } : {}),
      ...(isAdmin && companyApplyOnly ? { company_can_apply_only: true } : {}),
    };

    const fetch = isAdmin ? listLeadTracking(params) : listMyLeadTracking(params);

    fetch
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotalCount(data.total_count);
        setTotalPages(data.total_pages);
      })
      .catch(() => {
        if (!cancelled) { setItems([]); setTotalCount(0); setTotalPages(1); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isAdmin, page, statusFilter, profileFilter, tmFilter, companyApplyOnly]);

  async function handleCleanupArchived() {
    setConfirmCleanup(false);
    setCleaning(true);
    try {
      const { deleted } = await deleteArchivedTracking(isAdmin);
      setArchivedCount(0);
      if (deleted > 0) {
        setPage(1);
        setStatusFilter("new");
      }
    } finally {
      setCleaning(false);
    }
  }

  async function handleStatusChange(trackingId: number, status: string, notes?: string) {
    if (isAdmin) {
      await updateTrackingStatus(trackingId, status, notes);
    } else {
      await updateMyTrackingStatus(trackingId, status, notes);
    }
    setItems((prev) =>
      prev.map((r) => r.tracking_id === trackingId ? { ...r, status } : r)
    );
    // Keep archived badge count in sync
    if (status === "archived") {
      setArchivedCount((prev) => (prev != null ? prev + 1 : 1));
    }
  }

  const profileOptions = [
    { value: null as number | null, label: "All profiles" },
    ...targets.map((t) => ({ value: t.watch_target_id, label: t.display_name })),
  ];

  const selectedProfile = profileOptions.find((o) => o.value === profileFilter) ?? profileOptions[0];

  const tmOptions = [
    { value: null as number | null, label: "All TMs" },
    ...techManagers.map((tm) => ({ value: tm.id, label: tm.name })),
  ];
  const selectedTm = tmOptions.find((o) => o.value === tmFilter) ?? tmOptions[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground font-inter">
          {totalCount} lead{totalCount !== 1 ? "s" : ""} tracked
        </p>
        <ViewToggle view={view} onChange={switchView} />
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={selectedProfile}
          onChange={(opt) => { setProfileFilter(opt?.value ?? null); setPage(1); }}
          options={profileOptions}
          isSearchable
          placeholder="All profiles"
          styles={SELECT_STYLES}
          className="min-w-[200px]"
        />

        {isAdmin && (
          <Select
            value={selectedTm}
            onChange={(opt) => { setTmFilter(opt?.value ?? null); setPage(1); }}
            options={tmOptions}
            isSearchable
            placeholder="All TMs"
            styles={SELECT_STYLES}
            className="min-w-[180px]"
          />
        )}

        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
            <input
              type="checkbox"
              checked={companyApplyOnly}
              onChange={(e) => { setCompanyApplyOnly(e.target.checked); setPage(1); }}
              className="rounded border-border bg-surface-muted text-amber-500 focus:ring-amber-500"
            />
            Direct to Company only
          </label>
        )}
      </div>

      {/* Status chips — a status filter doesn't make sense in board view,
          since the board already shows every status as its own column */}
      <div className="flex flex-wrap items-center gap-2">
        {view === "list" && (
          <>
            <span className="text-xs text-muted-foreground font-inter shrink-0">Status</span>
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => { setStatusFilter(o.value); setPage(1); }}
                className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-colors ${
                  statusFilter === o.value
                    ? STATUS_CHIP_SELECTED[o.value] ?? "bg-slate-500 text-white border-slate-400"
                    : "bg-surface-muted/60 text-muted-foreground border-border hover:border-border hover:text-foreground"
                }`}
              >
                {o.label}
              </button>
            ))}
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          {archivedCount != null && archivedCount > 0 && (
            <span className="text-xs text-muted-foreground font-inter bg-surface-muted border border-border rounded-lg px-2 py-1">
              {archivedCount} archived
            </span>
          )}
          <button
            onClick={() => setConfirmCleanup(true)}
            disabled={cleaning || archivedCount === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive hover:text-destructive-foreground border border-destructive/50 hover:border-destructive hover:bg-destructive rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-inter"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {cleaning ? "Deleting…" : "Clean Up Archived"}
          </button>
        </div>
      </div>

      {/* Board view */}
      {view === "board" && (
        <StatusKanbanBoard
          columns={BOARD_COLUMNS}
          getId={(r) => r.tracking_id}
          getStatus={(r) => r.status}
          withStatus={(r, status) => ({ ...r, status })}
          fetchColumnItems={fetchBoardColumn}
          onStatusChange={(r, status) => handleStatusChange(r.tracking_id, status)}
          renderCard={(r) => (
            <LeadCard lead={r} onClick={() => setSelectedRecord(r)} compact />
          )}
        />
      )}

      {/* Loading */}
      {view === "list" && loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty */}
      {view === "list" && !loading && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground font-inter">
          <p className="text-sm">No tracking records found.</p>
          <p className="text-xs mt-1 text-muted-foreground">
            Leads are tracked per TM once a profile is assigned.
          </p>
        </div>
      )}

      {/* Grid */}
      {view === "list" && !loading && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((record) => (
            <LeadCard
              key={record.tracking_id}
              lead={record}
              onClick={() => setSelectedRecord(record)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {view === "list" && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page <= 1}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-muted-foreground font-inter">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Cleanup confirm modal */}
      {confirmCleanup && (
        <ConfirmModal
          title="Delete Archived Tracking Records"
          message={`Delete all ${archivedCount ?? ""} archived tracking records? This cannot be undone.`}
          confirmLabel="Delete Archived"
          onConfirm={handleCleanupArchived}
          onClose={() => setConfirmCleanup(false)}
        />
      )}

      {/* Detail modal */}
      {selectedRecord && (
        <LeadDetailModal
          record={selectedRecord}
          isAdmin={isAdmin}
          onStatusChange={async (trackingId, status, notes) => {
            await handleStatusChange(trackingId, status, notes);
            setSelectedRecord((prev) =>
              prev?.tracking_id === trackingId ? { ...prev, status } : prev
            );
          }}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
}
